import React, {PureComponent} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {RecipientRolesEnum} from '../../../../libs/shared/core/src/models/api/recipientModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {NewPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualRecipientMap} from '../../../helpers/models/recipientHelpers';
import {ConversationModel} from '../../../models/api/conversationModel';
import {ComposerHeaderBanner} from './banner/composerHeaderBanner';
import {ComposerDetails, ComposerDetailsPublicProps, FocusModesEnum} from './composerDetails';
import {EmailComposerMore} from './emailComposerMore';
import {EmailComposerSubject} from './emailComposerSubject';

/*
 * Props.
 */

export interface EmailComposerHeaderProps extends ComposerDetailsPublicProps {
  recipients: VisualRecipientMap;
  conversation: ConversationModel | null;
  user: TeammateModel;
  subject: string;
  onSubjectChange: (arg: string) => void;
  onSubjectBlur: () => void;
  onPopout: (() => void) | undefined;
  isComposerPopup: boolean;
  panelId: NewPanelId;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto;
  grid-template-areas:
    'banner'
    'scroll';
  border-bottom: 1px solid ${(p) => p.theme.message.border};
`;

interface ScrollStyleProps {
  $isPopup: boolean;
}
const StyledScrollDiv = styled.div<ScrollStyleProps>`
  grid-area: scroll;
  display: grid;
  align-items: flex-start;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'recipients more'
    'recipients .';
  overflow-x: auto;
  max-height: ${(p) => (p.$isPopup ? '127px' : 'unset')};
`;

const StyledBannerDiv = styled.div`
  grid-area: banner;
  padding-bottom: 10px;
`;

/*
 * Component.
 */

interface EmailComposerHeaderState {
  isCcVisible: boolean;
  isBccVisible: boolean;
  isSubjectVisible: boolean;
  isForwardBannerVisible: boolean;
  canMaybeShowHelpfulHintBanners: boolean;
  focusMode?: FocusModesEnum;
  focusKey: number;
}

export class EmailComposerHeader extends PureComponent<EmailComposerHeaderProps, EmailComposerHeaderState> {
  constructor(props: EmailComposerHeaderProps) {
    super(props);
    const {message} = props;
    this.state = {
      isCcVisible: false,
      isBccVisible: false,
      isSubjectVisible: !message.inReplyToId,
      isForwardBannerVisible: false,
      canMaybeShowHelpfulHintBanners: false,
      focusMode: props.focusMode,
      focusKey: 0,
    };
  }

  /*
   * Event handlers.
   */

  private readonly showCc = () => {
    this.setState(({focusKey}) => ({
      isCcVisible: true,
      focusMode: FocusModesEnum.CC,
      focusKey: focusKey + 1,
    }));
  };

  private readonly showBcc = () => {
    this.setState(({focusKey}) => ({
      isBccVisible: true,
      focusMode: FocusModesEnum.BCC,
      focusKey: focusKey + 1,
    }));
  };

  private readonly showSubject = () => {
    this.setState(({focusKey}) => ({
      isSubjectVisible: true,
      focusMode: FocusModesEnum.SUBJECT,
      focusKey: focusKey + 1,
    }));
  };

  /*
   * Lifecycle.
   */

  static getDerivedStateFromProps(
    nextProps: Readonly<EmailComposerHeaderProps>,
    prevState: Readonly<EmailComposerHeaderState>,
  ) {
    const {recipients} = nextProps;
    return {
      isCcVisible: prevState.isCcVisible || findIsSectionVisible(recipients, RecipientRolesEnum.CC),
      isBccVisible: prevState.isBccVisible || findIsSectionVisible(recipients, RecipientRolesEnum.BCC),
    };
  }

  componentDidUpdate(previousProps: Readonly<EmailComposerHeaderProps>) {
    this.maybeShowSubject(previousProps.subject);
  }

  // Canned responses can change the subject of the reply.  This function checks to see if the subject has changed,
  // and ensures that the subject will be visible if it has.
  private maybeShowSubject(prevSubject?: string) {
    const {subject} = this.props;

    if (!subject || !prevSubject || subject === prevSubject) {
      return;
    }

    this.setState(({isSubjectVisible}) => {
      if (isSubjectVisible) {
        return null;
      }

      return {isSubjectVisible: true};
    });
  }

  /*
   * Render.
   */

  render() {
    const {onPopout, isComposerPopup} = this.props;
    const {focusMode, focusKey} = this.state;
    const keyHandlers = {
      [KeyboardActionsEnum.EDITOR_CC]: this.showCc,
      [KeyboardActionsEnum.EDITOR_BCC]: this.showBcc,
      [KeyboardActionsEnum.EDITOR_SUBJECT]: this.showSubject,
    };

    return (
      <KeyboardShortcuts handlers={keyHandlers}>
        <StyledHeaderDiv>
          <StyledBannerDiv>{this.maybeRenderComposerHeaderBanner()}</StyledBannerDiv>

          <StyledScrollDiv $isPopup={isComposerPopup}>
            <EmailComposerMore
              {...this.state}
              showCc={this.showCc}
              showBcc={this.showBcc}
              showSubject={this.showSubject}
              onPopout={onPopout}
            />
            <ComposerDetails {...this.props} {...this.state} />
          </StyledScrollDiv>
        </StyledHeaderDiv>
        {this.state.isSubjectVisible && (
          <EmailComposerSubject
            {...this.props}
            shouldFocus={focusMode === FocusModesEnum.SUBJECT}
            focusKey={focusKey}
          />
        )}
      </KeyboardShortcuts>
    );
  }

  maybeRenderComposerHeaderBanner() {
    const {message, conversation, user, panelId} = this.props;
    if (!conversation) {
      return null;
    }

    return (
      <ComposerHeaderBanner
        message={message}
        conversation={conversation}
        teammateId={user.id}
        panelId={panelId}
      />
    );
  }
}

/*
 * Helpers.
 */

function findIsSectionVisible(recipients: VisualRecipientMap, role: RecipientRolesEnum) {
  const recipientsWithRole = recipients[role];
  return Boolean(recipientsWithRole && recipientsWithRole.length > 0);
}
